import type { MaskedGameState, MaskedHolding, MaskedPlayer } from '@completto/shared';
import { findAllPlacements, rotationCandidates } from '@completto/shared';
import { ActionButtonsView } from '../components/Game/ActionButtons.ts';
import { type ActionGuideMode, ActionGuideView } from '../components/Game/ActionGuide.ts';
import { BonusBannerView } from '../components/Game/BonusBanner.ts';
import { BonusFlashView } from '../components/Game/BonusFlash.ts';
import { CenterAreaView } from '../components/Game/CenterArea.ts';
import { GameHeaderView } from '../components/Game/GameHeader.ts';
import { HoldingCardView } from '../components/Game/HoldingCard.ts';
import { LeaveConfirmModal } from '../components/Game/LeaveConfirmModal.ts';
import { MyLaneView } from '../components/Game/MyLane.ts';
import { OpenPoolView } from '../components/Game/OpenPool.ts';
import { OpponentPanelView } from '../components/Game/OpponentPanel.ts';
import { RotationModalView } from '../components/Game/RotationModal.ts';
import { WinCelebrationView } from '../components/Game/WinCelebration.ts';
import { tapImpact } from '../lib/haptics.ts';
import { log } from '../lib/logger.ts';
import { toLogicalPoint } from '../lib/orientation-lock.ts';
import type { GameOrchestrator } from '../services/orchestrator.ts';
import { pushToast, setPoolFilter, uiStore } from '../stores/index.ts';
import { gapFromCellAndPoint, gapToTargetIdx } from './insert-drag.ts';

type UiMode =
  | { kind: 'IDLE' }
  | { kind: 'PLACEMENT'; selectedIdx: number | null; resolvedNumber: number | null }
  | { kind: 'ROTATION_PICK'; targetIdx: number; candidates: number[] }
  | {
      kind: 'INSERT_DRAGGING';
      fromIdx: number;
      pointerId: number;
      hoverGap: number | null;
    };

const DRAG_THRESHOLD_PX = 5;

const JUST_PLACED_FLASH_MS = 1500;

const FINISHED_NAVIGATE_DELAY_MS = 3000;

const AUTO_DISCARD_DELAY_MS = 700;

export type GamePageOptions = {
  onFinished?: () => void;
  onLeave?: () => void;
  isMultiplayer?: boolean;
};

export class GamePage {
  private root: HTMLElement;
  private service: GameOrchestrator;
  private state: MaskedGameState | null;
  private uiMode: UiMode = { kind: 'IDLE' };
  private justPlacedIdx: number | null = null;
  private justPlacedTimer: number | null = null;
  private unsubscribe: (() => void) | null = null;
  private unsubscribeUi: (() => void) | null = null;
  private finishedTimer: number | null = null;
  private finishedTriggered = false;
  private onFinished: (() => void) | null;
  private onLeave: (() => void) | null;
  private isMultiplayer: boolean;
  private leaveConfirm: LeaveConfirmModal | null = null;
  private prevBonusActive = false;
  private bonusFlashKey = 0;
  private autoDiscardTimer: number | null = null;
  private autoDiscarding = false;

  // Drag session 임시 상태 (re-render 발생 안 함)
  private dragOriginX = 0;
  private dragOriginY = 0;
  private dragMoved = false;
  private dragGhost: HTMLElement | null = null;
  private dragPointerHandlersBound = false;

  constructor(root: HTMLElement, service: GameOrchestrator, options: GamePageOptions = {}) {
    this.root = root;
    this.root.dataset.gameRoot = '';
    this.service = service;
    this.state = service.getState();
    this.onFinished = options.onFinished ?? null;
    this.onLeave = options.onLeave ?? null;
    this.isMultiplayer = options.isMultiplayer ?? false;
  }

  mount(): void {
    this.unsubscribe = this.service.subscribe((s) => {
      const prev = this.state;
      this.state = s;
      // 드래그 중에 외부 state 변경이 들어오면 드래그 취소
      if (this.uiMode.kind === 'INSERT_DRAGGING') {
        this.cancelDrag();
      }
      this.reconcileUiMode(prev, s);
      // 보너스 false → true 전이 감지 (1회 flash)
      if (!this.prevBonusActive && s.bonusActive) {
        this.bonusFlashKey++;
        void tapImpact('medium');
      }
      this.prevBonusActive = s.bonusActive;
      this.checkFinished();
      this.maybeScheduleAutoDiscard();
      this.render();
    });
    this.unsubscribeUi = uiStore.subscribe((next, prev) => {
      if (next.poolFilter !== prev.poolFilter || next.sendingMove !== prev.sendingMove) {
        this.render();
      }
    });
    if (import.meta.env.DEV) {
      window.addEventListener('keydown', this.handleCheatKey);
    }
    this.leaveConfirm = new LeaveConfirmModal(document.body);
    this.leaveConfirm.mount();
    this.checkFinished();
    this.render();
  }

  unmount(): void {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
    if (this.unsubscribeUi) {
      this.unsubscribeUi();
      this.unsubscribeUi = null;
    }
    if (this.justPlacedTimer !== null) {
      window.clearTimeout(this.justPlacedTimer);
      this.justPlacedTimer = null;
    }
    if (this.finishedTimer !== null) {
      window.clearTimeout(this.finishedTimer);
      this.finishedTimer = null;
    }
    this.cancelAutoDiscard();
    if (import.meta.env.DEV) {
      window.removeEventListener('keydown', this.handleCheatKey);
    }
    if (this.leaveConfirm) {
      this.leaveConfirm.unmount();
      this.leaveConfirm = null;
    }
    this.cancelDrag();
    this.unbindGlobalPointerHandlers();
  }

  private handleCheatKey = (e: KeyboardEvent): void => {
    if (!e.shiftKey || e.key !== 'F') return;
    if (!this.service.forceFinish) return;
    e.preventDefault();
    log.info('cheat.force-finish');
    this.service.forceFinish();
  };

  private handleLeaveClick = (): void => {
    const onLeave = this.onLeave;
    if (!onLeave) return;
    this.leaveConfirm?.open({
      isMultiplayer: this.isMultiplayer,
      onConfirm: () => onLeave(),
    });
  };

  private checkFinished(): void {
    if (this.finishedTriggered) return;
    if (!this.state || this.state.status !== 'FINISHED') return;
    if (!this.onFinished) return;
    this.finishedTriggered = true;
    const callback = this.onFinished;
    this.finishedTimer = window.setTimeout(() => {
      this.finishedTimer = null;
      callback();
    }, FINISHED_NAVIGATE_DELAY_MS);
  }

  private reconcileUiMode(prev: MaskedGameState | null, next: MaskedGameState): void {
    if (this.uiMode.kind === 'INSERT_DRAGGING') return;
    const myId = this.service.myPlayerId;
    const isMyTurn = next.players[next.currentPlayerIdx]?.id === myId;
    const turnChanged = prev !== null && prev.currentPlayerIdx !== next.currentPlayerIdx;

    if (!isMyTurn) {
      if (this.uiMode.kind === 'PLACEMENT' || this.uiMode.kind === 'ROTATION_PICK') {
        this.uiMode = { kind: 'IDLE' };
      }
      return;
    }
    if (turnChanged && this.uiMode.kind === 'ROTATION_PICK') {
      this.uiMode = { kind: 'IDLE' };
      return;
    }
    if (next.holding === null && this.uiMode.kind === 'PLACEMENT') {
      this.uiMode = { kind: 'IDLE' };
    }
  }

  private maybeScheduleAutoDiscard(): void {
    if (this.autoDiscarding || this.autoDiscardTimer !== null) return;
    if (!this.shouldAutoDiscard()) return;
    this.autoDiscardTimer = window.setTimeout(() => {
      this.autoDiscardTimer = null;
      if (!this.shouldAutoDiscard()) return;
      void this.runAutoDiscard();
    }, AUTO_DISCARD_DELAY_MS);
  }

  private cancelAutoDiscard(): void {
    if (this.autoDiscardTimer !== null) {
      window.clearTimeout(this.autoDiscardTimer);
      this.autoDiscardTimer = null;
    }
    this.autoDiscarding = false;
  }

  private shouldAutoDiscard(): boolean {
    const state = this.state;
    if (!state || state.status === 'FINISHED') return false;
    if (this.uiMode.kind === 'INSERT_DRAGGING') return false;
    const me = this.myPlayer();
    if (!me) return false;
    const isMyTurn = state.players[state.currentPlayerIdx]?.id === me.id;
    if (!isMyTurn) return false;
    const holding = state.holding;
    if (!holding || holding.number === null) return false;
    // 자동 DISCARD는 A-1(덱 출처)에만 적용. 풀 출처 holding은 룰상 PLACE 가능 자리가
    // 보장되며, 설령 그렇지 못해도 풀로 다시 보낼 수 없으므로 자동 처리 금지.
    if (holding.source === 'POOL') return false;
    const placements = findAllPlacements(me.lane, {
      number: holding.number,
      resolvedNumber: holding.resolvedNumber,
    });
    return placements.length === 0;
  }

  private async runAutoDiscard(): Promise<void> {
    this.autoDiscarding = true;
    try {
      await this.service.sendMove({ type: 'DISCARD_TO_POOL' });
      this.uiMode = { kind: 'IDLE' };
      pushToast('놓을 곳이 없어 풀로 보냈어요', 'info');
      this.render();
    } catch (err) {
      pushToast('자동 풀 보내기 실패', 'error');
      log.error('auto-discard failed', err);
    } finally {
      this.autoDiscarding = false;
    }
  }

  // === Action handlers ===

  private handleDraw = async (): Promise<void> => {
    const state = this.state;
    if (!state) return;
    if (state.holding !== null) return;
    if (state.center.deckCount === 0) {
      pushToast('더미가 비어있습니다', 'warn');
      return;
    }
    try {
      await this.service.sendMove({ type: 'DRAW_FROM_DECK' });
      this.uiMode = { kind: 'PLACEMENT', selectedIdx: null, resolvedNumber: null };
      this.render();
    } catch (err) {
      pushToast('뽑기 실패', 'error');
      log.error('draw failed', err);
    }
  };

  private handlePoolChipClick = async (tileId: string): Promise<void> => {
    const state = this.state;
    if (!state) return;
    if (state.holding !== null) {
      pushToast('이미 손에 든 타일이 있습니다', 'warn');
      return;
    }
    const me = this.myPlayer();
    if (!me) return;
    const tile = state.center.openPool.find((t) => t.id === tileId);
    if (!tile || tile.number === null) return;
    const placements = findAllPlacements(me.lane, {
      number: tile.number,
      resolvedNumber: tile.resolvedNumber,
    });
    if (placements.length === 0) {
      pushToast('둘 곳이 없는 타일입니다', 'warn');
      return;
    }
    try {
      await this.service.sendMove({ type: 'TAKE_FROM_POOL', tileId });
      this.uiMode = { kind: 'PLACEMENT', selectedIdx: null, resolvedNumber: null };
      this.render();
    } catch (err) {
      pushToast('가져오기 실패', 'error');
      log.error('take failed', err);
    }
  };

  private handlePoolButton = (): void => {
    pushToast('풀에서 가져올 타일을 탭하세요', 'info');
  };

  private handleLaneCellClick = (idx: number): void => {
    const state = this.state;
    if (!state) return;
    const mode = this.uiMode;
    if (mode.kind === 'PLACEMENT') {
      const tile = state.holding;
      if (!tile || tile.number === null) return;
      // 이미 선택된 cell을 재클릭 → 즉시 확정 (회전 결정이 끝난 경우만)
      if (mode.selectedIdx === idx && mode.resolvedNumber !== null) {
        this.handleConfirm();
        return;
      }
      const candidates =
        tile.resolvedNumber !== null ? [tile.resolvedNumber] : rotationCandidates(tile.number);
      if (candidates.length > 1) {
        this.uiMode = { kind: 'ROTATION_PICK', targetIdx: idx, candidates };
        this.render();
        return;
      }
      const resolvedNumber = candidates[0];
      if (resolvedNumber === undefined) return;
      this.uiMode = { kind: 'PLACEMENT', selectedIdx: idx, resolvedNumber };
      this.maybeHintTapToConfirm();
      this.render();
    }
    // SWAP은 더 이상 클릭으로 처리하지 않음 — 드래그로만
  };

  private maybeHintTapToConfirm(): void {
    const KEY = 'hint:tap-to-confirm:shown';
    if (sessionStorage.getItem(KEY) === '1') return;
    sessionStorage.setItem(KEY, '1');
    pushToast('한 번 더 탭하면 배치가 확정됩니다', 'info');
  }

  private renderHoldingStripLandscape(holding: MaskedHolding): HTMLElement {
    const fromPool = holding.source === 'POOL';
    const display = holding.resolvedNumber ?? holding.number ?? '·';
    const candidates = holding.number !== null ? rotationCandidates(holding.number) : [];
    const rotatable = candidates.length > 1 && holding.resolvedNumber === null;
    const altText =
      rotatable && candidates[1] !== undefined ? `${candidates[0]} 또는 ${candidates[1]}` : null;
    const showFlowHint =
      this.uiMode.kind === 'PLACEMENT' &&
      this.uiMode.selectedIdx === null &&
      holding.number !== null;

    const strip = document.createElement('div');
    strip.className =
      'holding-strip flex items-center gap-2 px-2 py-1.5 rounded-xl bg-card-gold/10 ring-1 ring-card-gold/40 shrink-0 min-w-0 whitespace-nowrap overflow-hidden';
    strip.setAttribute('aria-label', `손에 든 타일 ${display}`);

    const tile = document.createElement('div');
    tile.className =
      'flex items-center justify-center w-11 h-12 rounded-md bg-card-navy text-card-gold text-lg font-extrabold shadow-sm shrink-0';
    tile.textContent = String(display);
    strip.appendChild(tile);

    const meta = document.createElement('div');
    meta.className = 'flex flex-col min-w-0 grow leading-tight overflow-hidden';
    const label = document.createElement('span');
    label.className = 'text-[11px] text-card-brass font-semibold truncate';
    label.textContent = fromPool ? '풀에서 가져옴' : '더미에서 뽑음';
    meta.appendChild(label);
    if (altText) {
      const rot = document.createElement('span');
      rot.className = 'text-[10px] text-wood-coffee/70 truncate';
      const rotIcon = document.createElement('span');
      rotIcon.className = 'text-base align-middle';
      rotIcon.textContent = '⟲';
      rot.appendChild(rotIcon);
      rot.appendChild(document.createTextNode(` ${altText}`));
      meta.appendChild(rot);
    }
    strip.appendChild(meta);

    if (showFlowHint) {
      const arrow = document.createElement('span');
      arrow.className =
        'flow-hint text-card-gold text-base leading-none animate-bounce select-none shrink-0';
      arrow.setAttribute('aria-hidden', 'true');
      arrow.textContent = '↓';
      strip.appendChild(arrow);
    }

    if (!fromPool) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className =
        'inline-flex items-center justify-center rounded-lg px-2 py-1 text-[11px] font-semibold bg-wood-paper hover:bg-wood-cream text-wood-coffee ring-1 ring-wood-sand shadow-sm hover:shadow active:scale-[0.98] transition cursor-pointer shrink-0';
      btn.setAttribute('aria-label', '현재 손에 든 타일을 풀로 버리기');
      btn.textContent = '🗑 풀로';
      btn.addEventListener('click', () => {
        this.handleDiscard();
      });
      strip.appendChild(btn);
    }

    return strip;
  }

  private handleRotationPick = (resolvedNumber: number): void => {
    const mode = this.uiMode;
    if (mode.kind !== 'ROTATION_PICK') return;
    this.uiMode = {
      kind: 'PLACEMENT',
      selectedIdx: mode.targetIdx,
      resolvedNumber,
    };
    this.render();
  };

  private handleConfirm = async (): Promise<void> => {
    const mode = this.uiMode;
    if (mode.kind !== 'PLACEMENT') return;
    if (mode.selectedIdx === null || mode.resolvedNumber === null) return;
    const targetIdx = mode.selectedIdx;
    try {
      await this.service.sendMove({
        type: 'PLACE',
        resolvedNumber: mode.resolvedNumber,
        targetIdx,
      });
      this.flashJustPlaced(targetIdx);
      void tapImpact('light');
      this.uiMode = { kind: 'IDLE' };
      this.render();
    } catch (err) {
      pushToast('배치 실패', 'error');
      log.error('place failed', err);
    }
  };

  private handleDiscard = async (): Promise<void> => {
    const state = this.state;
    if (!state) return;
    const tile = state.holding;
    if (!tile || tile.number === null) return;
    if (tile.source === 'POOL') {
      // 풀에서 가져온 타일은 다시 풀로 보낼 수 없다 (룰 §4 A-2 + 의미 없음).
      pushToast('풀에서 가져온 타일은 풀로 보낼 수 없습니다', 'warn');
      return;
    }
    try {
      await this.service.sendMove({ type: 'DISCARD_TO_POOL' });
      this.uiMode = { kind: 'IDLE' };
      this.render();
    } catch (err) {
      pushToast('풀로 보내기 실패', 'error');
      log.error('discard failed', err);
    }
  };

  private handleCancel = (): void => {
    if (this.uiMode.kind === 'INSERT_DRAGGING') {
      this.cancelDrag();
      return;
    }
    // 자리 선택 취소 시: holding이 잡혀있으면 PLACEMENT(자리 미선택) = HOLDING 모드로 돌아가야
    // 액션 버튼이 사라지지 않음. holding 없으면 진짜 IDLE.
    if (this.state?.holding) {
      this.uiMode = { kind: 'PLACEMENT', selectedIdx: null, resolvedNumber: null };
    } else {
      this.uiMode = { kind: 'IDLE' };
    }
    this.render();
  };

  private async commitInsert(fromIdx: number, gap: number): Promise<void> {
    const toIdx = gapToTargetIdx(fromIdx, gap);
    if (toIdx === null) {
      this.uiMode = { kind: 'IDLE' };
      this.render();
      return;
    }
    try {
      await this.service.sendMove({ type: 'SHUFFLE_DOWN_TILE', fromIdx, toIdx });
      this.uiMode = { kind: 'IDLE' };
      this.render();
    } catch (err) {
      pushToast('위치 바꾸기 실패', 'error');
      log.error('insert failed', err);
    }
  }

  private flashJustPlaced(idx: number): void {
    this.justPlacedIdx = idx;
    if (this.justPlacedTimer !== null) window.clearTimeout(this.justPlacedTimer);
    this.justPlacedTimer = window.setTimeout(() => {
      this.justPlacedIdx = null;
      this.justPlacedTimer = null;
      this.render();
    }, JUST_PLACED_FLASH_MS);
  }

  // === Drag (pointer events) ===

  private handleLaneCellPointerDown = (
    idx: number,
    pointerId: number,
    clientX: number,
    clientY: number,
    el: HTMLElement,
  ): void => {
    if (!this.canStartInsertDrag(idx)) return;

    this.uiMode = { kind: 'INSERT_DRAGGING', fromIdx: idx, pointerId, hoverGap: null };
    this.dragOriginX = clientX;
    this.dragOriginY = clientY;
    this.dragMoved = false;
    try {
      el.setPointerCapture(pointerId);
    } catch {
      // ignore
    }
    this.bindGlobalPointerHandlers();
    this.render();
  };

  private canStartInsertDrag(idx: number): boolean {
    const state = this.state;
    if (!state) return false;
    const me = this.myPlayer();
    if (!me) return false;
    if (state.holding) return false;
    const isMyTurn = state.players[state.currentPlayerIdx]?.id === me.id;
    if (!isMyTurn) return false;
    if (state.status !== 'IN_PROGRESS') return false;
    const slot = me.lane.slots[idx];
    if (!slot || slot.tile.face !== 'DOWN') return false;
    // 비공개 타일이 단 1개여도 끼워넣기 가능 (위치 재배치는 항상 의미 있음)
    return this.uiMode.kind === 'IDLE' || this.uiMode.kind === 'INSERT_DRAGGING';
  }

  private bindGlobalPointerHandlers(): void {
    if (this.dragPointerHandlersBound) return;
    document.addEventListener('pointermove', this.onGlobalPointerMove);
    document.addEventListener('pointerup', this.onGlobalPointerUp);
    document.addEventListener('pointercancel', this.onGlobalPointerUp);
    this.dragPointerHandlersBound = true;
  }

  private unbindGlobalPointerHandlers(): void {
    if (!this.dragPointerHandlersBound) return;
    document.removeEventListener('pointermove', this.onGlobalPointerMove);
    document.removeEventListener('pointerup', this.onGlobalPointerUp);
    document.removeEventListener('pointercancel', this.onGlobalPointerUp);
    this.dragPointerHandlersBound = false;
  }

  private onGlobalPointerMove = (e: PointerEvent): void => {
    const mode = this.uiMode;
    if (mode.kind !== 'INSERT_DRAGGING') return;
    if (e.pointerId !== mode.pointerId) return;

    // 회전 활성 시 ghost는 회전된 body 자식이므로 논리 좌표로 그려야 함.
    // elementFromPoint는 viewport 좌표계라 원본 시각 좌표를 그대로 사용.
    const logical = toLogicalPoint(e.clientX, e.clientY);

    if (!this.dragMoved) {
      const dx = e.clientX - this.dragOriginX;
      const dy = e.clientY - this.dragOriginY;
      if (Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
      this.dragMoved = true;
      this.spawnGhost(mode.fromIdx, logical.x, logical.y);
      this.updateHoverHighlight();
    } else if (this.dragGhost) {
      this.dragGhost.style.transform = `translate(${logical.x}px, ${logical.y}px) translate(-50%, -50%)`;
    }

    const newHoverGap = this.hitTestGapIdx(e.clientX, e.clientY);
    if (newHoverGap !== mode.hoverGap) {
      this.uiMode = { ...mode, hoverGap: newHoverGap };
      this.updateHoverHighlight();
    }
  };

  private onGlobalPointerUp = (e: PointerEvent): void => {
    const mode = this.uiMode;
    if (mode.kind !== 'INSERT_DRAGGING') return;
    if (e.pointerId !== mode.pointerId) return;

    const targetGap = mode.hoverGap;
    const fromIdx = mode.fromIdx;
    const wasDragMoved = this.dragMoved;

    this.cleanupDrag();

    if (!wasDragMoved) {
      // 단순 클릭 (이동 임계값 미만) — 드래그 아님. IDLE 복귀, 클릭 핸들러는 별도 처리.
      this.uiMode = { kind: 'IDLE' };
      this.render();
      return;
    }

    if (targetGap !== null) {
      void this.commitInsert(fromIdx, targetGap);
    } else {
      // lane 밖에 떨군 경우
      this.uiMode = { kind: 'IDLE' };
      this.render();
    }
  };

  private cancelDrag(): void {
    this.cleanupDrag();
    if (this.uiMode.kind === 'INSERT_DRAGGING') {
      this.uiMode = { kind: 'IDLE' };
    }
  }

  private cleanupDrag(): void {
    this.unbindGlobalPointerHandlers();
    if (this.dragGhost) {
      this.dragGhost.remove();
      this.dragGhost = null;
    }
    // 모든 lane cell의 pointer-events 복원 (re-render 발생 안 한 경우 대비)
    this.root.querySelectorAll<HTMLElement>('[data-lane-cell-idx]').forEach((el) => {
      el.style.pointerEvents = '';
      el.classList.remove('ring-4', 'ring-card-gold', 'opacity-50');
    });
    this.dragMoved = false;
  }

  private spawnGhost(fromIdx: number, clientX: number, clientY: number): void {
    const me = this.myPlayer();
    const tile = me?.lane.slots[fromIdx]?.tile;
    if (!tile) return;
    const ghost = document.createElement('div');
    ghost.className =
      'fixed pointer-events-none z-50 w-11 h-14 rounded-md bg-gradient-to-br from-wood-bark to-wood-coffee ring-2 ring-card-gold shadow-2xl flex items-center justify-center text-wood-paper/40 text-2xl font-bold';
    ghost.textContent = '?';
    ghost.style.left = '0';
    ghost.style.top = '0';
    ghost.style.transform = `translate(${clientX}px, ${clientY}px) translate(-50%, -50%)`;
    document.body.appendChild(ghost);
    this.dragGhost = ghost;
  }

  private hitTestGapIdx(x: number, y: number): number | null {
    const target = document.elementFromPoint(x, y);
    if (!target) return null;

    // 1) 직접 셀 위 → 좌/우 절반으로 gap 결정.
    const cellEl = (target as Element).closest('[data-lane-cell-idx]') as HTMLElement | null;
    if (cellEl) {
      const idxStr = cellEl.dataset.laneCellIdx;
      if (idxStr !== undefined) {
        const idx = Number(idxStr);
        if (Number.isFinite(idx)) {
          const rect = cellEl.getBoundingClientRect();
          return gapFromCellAndPoint(idx, rect, x);
        }
      }
    }

    // 2) lane row 내부지만 셀 사이 여백 → 가장 가까운 셀의 좌/우로 fall back.
    const laneEl = (target as Element).closest('.my-lane') as HTMLElement | null;

    // 3) lane 바깥(좌/우 끝 초과)이지만 y가 lane 범위 내 → lane을 직접 탐색.
    const resolvedLane =
      laneEl ?? (this.root.querySelector<HTMLElement>('.my-lane') as HTMLElement | null);
    if (!resolvedLane) return null;

    const laneRect = resolvedLane.getBoundingClientRect();
    if (y < laneRect.top || y > laneRect.bottom) return null;

    const cells = Array.from(resolvedLane.querySelectorAll<HTMLElement>('[data-lane-cell-idx]'));
    if (cells.length === 0) return null;

    // 커서가 첫 셀 왼쪽 끝보다 왼쪽이면 gap=0, 마지막 셀 오른쪽 끝보다 오른쪽이면 gap=N.
    const firstRect = cells[0].getBoundingClientRect();
    if (x <= firstRect.left) return 0;
    const lastRect = cells[cells.length - 1].getBoundingClientRect();
    if (x >= lastRect.right) return cells.length;

    let bestCell: HTMLElement | null = null;
    let bestDist = Number.POSITIVE_INFINITY;
    for (const el of cells) {
      const r = el.getBoundingClientRect();
      const cx = (r.left + r.right) / 2;
      const d = Math.abs(x - cx);
      if (d < bestDist) {
        bestDist = d;
        bestCell = el;
      }
    }
    if (!bestCell) return null;
    const idxStr = bestCell.dataset.laneCellIdx;
    if (idxStr === undefined) return null;
    const idx = Number(idxStr);
    if (!Number.isFinite(idx)) return null;
    const rect = bestCell.getBoundingClientRect();
    return gapFromCellAndPoint(idx, rect, x);
  }

  private updateHoverHighlight(): void {
    // re-render 회피: 출발 셀(빈 자리) 표시와 gap 인디케이터를 DOM 직접 조작.
    const mode = this.uiMode;
    const hoverGap = mode.kind === 'INSERT_DRAGGING' ? mode.hoverGap : null;
    const from = mode.kind === 'INSERT_DRAGGING' ? mode.fromIdx : null;

    const allCells = this.root.querySelectorAll<HTMLElement>('[data-lane-cell-idx]');
    allCells.forEach((el) => {
      const idx = Number(el.dataset.laneCellIdx);
      el.classList.toggle('lane-cell-insert-source', from === idx);
      // 드래그 중인 출발 칸은 elementFromPoint hit-test에서 제외하기 위해 pointer-events 비활성화
      // (capture된 element도 elementFromPoint가 그 element를 우선 반환하는 버그 회피)
      if (from === idx) {
        el.style.pointerEvents = 'none';
      } else {
        el.style.pointerEvents = '';
      }
    });

    const allGaps = this.root.querySelectorAll<HTMLElement>('[data-lane-gap-idx]');
    allGaps.forEach((el) => {
      const gapIdx = Number(el.dataset.laneGapIdx);
      const isSelfGap = from !== null && (gapIdx === from || gapIdx === from + 1);
      const active = hoverGap !== null && hoverGap === gapIdx && !isSelfGap;
      el.classList.toggle('lane-gap-active', active);
    });
  }

  // === Render ===

  private myPlayer(): MaskedPlayer | undefined {
    if (!this.state) return undefined;
    return this.state.players.find((p) => p.id === this.service.myPlayerId);
  }

  private computeViewData() {
    const state = this.state;
    if (!state) return null;
    const me = this.myPlayer();
    if (!me) return null;
    const isMyTurn = state.players[state.currentPlayerIdx]?.id === me.id;
    const holding = state.holding;

    let placeableSlots = new Set<number>();
    const inZoneSlots = new Set<number>();

    if (this.uiMode.kind === 'PLACEMENT' && holding && holding.number !== null) {
      const all = findAllPlacements(me.lane, {
        number: holding.number,
        resolvedNumber: holding.resolvedNumber,
      });
      placeableSlots = new Set(all.map((p) => p.targetIdx));
      for (const idx of placeableSlots) inZoneSlots.add(idx);
    }

    const placeablePoolTileIds = new Set<string>();
    if (this.uiMode.kind === 'IDLE' && !holding) {
      for (const t of state.center.openPool) {
        if (t.number === null) continue;
        if (
          findAllPlacements(me.lane, { number: t.number, resolvedNumber: t.resolvedNumber })
            .length > 0
        ) {
          placeablePoolTileIds.add(t.id);
        }
      }
    }

    return {
      me,
      isMyTurn,
      holding,
      placeableSlots,
      inZoneSlots,
      placeablePoolTileIds,
    };
  }

  private render(): void {
    this.root.innerHTML = '';
    const state = this.state;
    if (!state) {
      this.renderLoading();
      return;
    }
    const data = this.computeViewData();
    if (!data) return;
    const { me, isMyTurn, holding, placeableSlots, inZoneSlots, placeablePoolTileIds } = data;

    // 가로(mlandscape:)면 3컬럼 grid (header / center | game | opponent),
    // 그 외(세로 + 데스크탑)는 기존 flex flex-col 스택.
    // 가로 모드는 viewport에 맞춰 잠금: h-[100dvh] (iOS Safari toolbar 변동 대응) + overflow-hidden.
    // lane의 가로 overflow는 자체 overflow-x-auto로 유지.
    const opponentsCount = state.players.filter((p) => p.id !== this.service.myPlayerId).length;
    const wrapper = document.createElement('div');
    const gridLayoutCls =
      opponentsCount === 0
        ? "mlandscape:grid-cols-[minmax(0,1fr)] mlandscape:[grid-template-areas:'header'_'pool'_'actions'_'lane']"
        : "mlandscape:grid-cols-[minmax(480px,1.4fr)_minmax(260px,360px)] mlandscape:[grid-template-areas:'header_header'_'opponent_pool'_'actions_actions'_'lane_lane']";
    wrapper.className =
      'game-page-wrapper min-h-screen safe-x flex flex-col gap-3 p-3 sm:p-4 max-w-screen-2xl mx-auto ' +
      `mlandscape:grid ${gridLayoutCls} mlandscape:grid-rows-[auto_minmax(0,1fr)_auto_auto] mlandscape:gap-2 mlandscape:p-2 ` +
      'mlandscape:h-[100dvh] mlandscape:max-h-[100dvh] mlandscape:w-[100dvw] mlandscape:max-w-[100dvw] mlandscape:overflow-hidden';

    // Header (가로: 전체 폭, 세로: 자연 흐름)
    const header = new GameHeaderView({
      currentPlayerNickname: state.players[state.currentPlayerIdx]?.nickname ?? '?',
      isMyTurn,
      deckCount: state.center.deckCount,
      poolCount: state.center.openPool.length,
      turnDeadline: state.turnDeadline,
      onLeaveClick: this.handleLeaveClick,
    });
    const headerEl = header.render();
    headerEl.classList.add('mlandscape:[grid-area:header]');
    wrapper.appendChild(headerEl);

    const poolEnabled =
      isMyTurn &&
      holding === null &&
      state.center.openPool.length > 0 &&
      this.uiMode.kind === 'IDLE';

    // 세로/데스크탑: 기존 2열(centerCol[CenterArea] + sidebarCol[OpponentPanel]).
    // 가로 모드에서는 hidden — 대신 duo(opponent/pool)를 wrapper의 grid에 직접 mount.
    const mainRow = document.createElement('div');
    mainRow.className = 'grid grid-cols-1 sm:grid-cols-12 gap-3 mlandscape:hidden';

    const centerCol = document.createElement('div');
    centerCol.className = 'sm:col-span-8';
    const centerArea = new CenterAreaView({
      poolTiles: state.center.openPool,
      placeablePoolTileIds,
      poolEnabled,
      poolFilter: uiStore.get().poolFilter,
      onPoolChipClick: this.handlePoolChipClick,
      onPoolFilterChange: setPoolFilter,
    });
    centerCol.appendChild(centerArea.render());
    mainRow.appendChild(centerCol);

    const sidebarCol = document.createElement('div');
    sidebarCol.className = 'sm:col-span-4';
    const opponentPanel = new OpponentPanelView({
      players: state.players,
      currentPlayerIdx: state.currentPlayerIdx,
      myPlayerId: this.service.myPlayerId,
      recentlyActedId: null,
    });
    sidebarCol.appendChild(opponentPanel.render());
    mainRow.appendChild(sidebarCol);
    wrapper.appendChild(mainRow);

    // 가로 모드 trio: opponent(좌) / pool(중) / deck(우)
    const opponentColLandscape = document.createElement('div');
    const opponentColHiddenCls = opponentsCount === 0 ? 'mlandscape:!hidden' : 'mlandscape:flex';
    opponentColLandscape.className = `hidden ${opponentColHiddenCls} mlandscape:[grid-area:opponent] mlandscape:min-w-0 mlandscape:min-h-0 mlandscape:overflow-y-auto mlandscape:p-2 mlandscape:flex-col`;
    const opponentPanelLandscape = new OpponentPanelView({
      players: state.players,
      currentPlayerIdx: state.currentPlayerIdx,
      myPlayerId: this.service.myPlayerId,
      recentlyActedId: null,
    });
    opponentColLandscape.appendChild(opponentPanelLandscape.render());
    wrapper.appendChild(opponentColLandscape);

    const poolColLandscape = document.createElement('div');
    poolColLandscape.className =
      'hidden mlandscape:flex mlandscape:[grid-area:pool] mlandscape:min-w-0 mlandscape:min-h-0 mlandscape:max-h-full mlandscape:overflow-hidden mlandscape:flex-col mlandscape:items-stretch mlandscape:justify-start mlandscape:px-3 mlandscape:py-2 mlandscape:gap-2';
    // OpenPool은 가운데 주인공으로 항상 노출. 내 차례에 holding 보유 시 위에 strip을 얹는다.
    if (isMyTurn && holding !== null && holding.number !== null) {
      poolColLandscape.appendChild(this.renderHoldingStripLandscape(holding));
    }
    const poolViewLandscape = new OpenPoolView({
      tiles: state.center.openPool,
      placeableTileIds: placeablePoolTileIds,
      enabled: poolEnabled,
      filter: uiStore.get().poolFilter,
      onChipClick: this.handlePoolChipClick,
      onFilterChange: setPoolFilter,
    });
    poolColLandscape.appendChild(poolViewLandscape.render());
    wrapper.appendChild(poolColLandscape);

    // 세로/데스크탑 전용: gameCol이 contents라 자식들이 wrapper 흐름에 직접 들어감.
    // 가로 모드에선 duo(opponent/pool)와 ActionButtons의 더미 뽑기가 역할을 대신하므로 hidden.
    const gameCol = document.createElement('div');
    gameCol.className = 'contents mlandscape:hidden';

    // Action guide
    const guide = new ActionGuideView({
      mode: this.computeGuideMode(),
      drawnNumber:
        holding?.resolvedNumber !== null && holding?.resolvedNumber !== undefined
          ? holding.resolvedNumber
          : (holding?.number ?? undefined),
      opponentNickname: !isMyTurn ? state.players[state.currentPlayerIdx]?.nickname : undefined,
    });
    gameCol.appendChild(guide.render());

    // Holding card — 항상 마운트 (없을 때 placeholder)로 layout shift 방지.
    // 타일이 들어올 때만 카드 안에서 fade-in.
    const holdingFromPool = holding !== null && holding.source === 'POOL';
    const holdingCard = new HoldingCardView({
      tile: holding,
      origin: holding ? holding.source : null,
      showFlowHint:
        this.uiMode.kind === 'PLACEMENT' &&
        this.uiMode.selectedIdx === null &&
        holding !== null &&
        holding.number !== null,
      prominent: true,
      onDiscard: holding !== null ? this.handleDiscard : undefined,
      discardEnabled: holding !== null && !holdingFromPool,
    });
    gameCol.appendChild(holdingCard.render());

    // Action buttons
    const buttonsMode = this.computeButtonsMode();
    const buttons = new ActionButtonsView({
      mode: buttonsMode,
      poolHasTiles: state.center.openPool.length > 0,
      deckHasTiles: state.center.deckCount > 0,
      sendingMove: uiStore.get().sendingMove,
      onClickDraw: this.handleDraw,
      onClickPool: this.handlePoolButton,
      onClickConfirm: this.handleConfirm,
      onClickCancel: this.handleCancel,
    });
    const buttonsEl = buttons.render();
    buttonsEl.classList.add('mlandscape:[grid-area:actions]', 'mlandscape:min-w-0');

    wrapper.appendChild(gameCol);
    wrapper.appendChild(buttonsEl);

    // My lane — 가로 모드에선 wrapper의 3번째 행(전체 폭) 차지.
    // 세로/데스크탑에선 wrapper의 자연 흐름.
    const isDragging = this.uiMode.kind === 'INSERT_DRAGGING';
    const insertFromIdx = this.uiMode.kind === 'INSERT_DRAGGING' ? this.uiMode.fromIdx : null;
    const lane = new MyLaneView({
      lane: me.lane,
      placeableSlots,
      inZoneSlots,
      selectedIdx: this.uiMode.kind === 'PLACEMENT' ? this.uiMode.selectedIdx : null,
      insertMode: isDragging ? 'INSERT_DRAG' : 'NONE',
      insertFromIdx,
      justPlacedIdx: this.justPlacedIdx,
      isMyTurn,
      canStartDrag: isMyTurn && !holding && state.status === 'IN_PROGRESS',
      onCellClick: this.handleLaneCellClick,
      onCellPointerDown: this.handleLaneCellPointerDown,
    });
    const laneEl = lane.render();
    laneEl.classList.add('mlandscape:[grid-area:lane]', 'mlandscape:min-w-0');
    wrapper.appendChild(laneEl);

    this.root.appendChild(wrapper);

    if (state.bonusActive) {
      const banner = new BonusBannerView({ active: true });
      this.root.appendChild(banner.render());
    }

    // 보너스 발동 순간 1회 flash. bonusFlashKey가 매 발동마다 증가하면
    // rune-ts가 새 element로 mount해 animation 재실행.
    if (this.bonusFlashKey > 0 && state.bonusActive) {
      const flash = new BonusFlashView({ flashKey: this.bonusFlashKey });
      this.root.appendChild(flash.render());
    }

    if (this.uiMode.kind === 'ROTATION_PICK') {
      const modal = new RotationModalView({
        candidates: this.uiMode.candidates,
        onPick: this.handleRotationPick,
        onCancel: this.handleCancel,
      });
      this.root.appendChild(modal.render());
    }

    if (state.status === 'FINISHED' && state.winnerId) {
      const winner = state.players.find((p) => p.id === state.winnerId);
      const isMe = winner?.id === this.service.myPlayerId;
      const celebration = new WinCelebrationView({ isMe });
      this.root.appendChild(celebration.render());

      const banner = document.createElement('div');
      banner.className =
        'fixed inset-x-0 mx-auto max-w-sm rounded-xl bg-card-gold text-wood-ink px-4 py-3 text-center font-semibold shadow-lg z-[60]';
      banner.style.bottom = 'max(1.5rem, calc(env(safe-area-inset-bottom) + 0.5rem))';
      banner.textContent = winner
        ? `${winner.nickname} 승리. 곧 결과 화면으로 이동합니다…`
        : '게임 종료. 곧 결과 화면으로 이동합니다…';
      this.root.appendChild(banner);
    }
  }

  private renderLoading(): void {
    const wrapper = document.createElement('main');
    wrapper.className = 'min-h-screen flex items-center justify-center text-wood-coffee/70';
    wrapper.textContent = '게임 상태를 기다리는 중…';
    this.root.appendChild(wrapper);
  }

  private computeGuideMode(): ActionGuideMode {
    const state = this.state;
    if (!state) return 'IDLE';
    const me = this.myPlayer();
    if (!me) return 'IDLE';
    if (state.status === 'FINISHED') return 'GAME_OVER';
    const isMyTurn = state.players[state.currentPlayerIdx]?.id === this.service.myPlayerId;
    if (!isMyTurn) return 'OPPONENT_TURN';
    if (state.bonusActive) return 'BONUS';

    const mode = this.uiMode;
    if (mode.kind === 'INSERT_DRAGGING') return 'INSERT_DRAG';
    if (mode.kind === 'PLACEMENT') {
      const holding = state.holding;
      if (!holding || holding.number === null) return 'IDLE';
      const placements = findAllPlacements(me.lane, {
        number: holding.number,
        resolvedNumber: holding.resolvedNumber,
      });
      if (placements.length === 0) return 'NO_PLACEMENT';
      if (mode.selectedIdx !== null) return 'PLACEMENT_SELECTED';
      return holding.source === 'DECK' ? 'HOLDING_DRAWN' : 'HOLDING_TAKEN';
    }
    return 'IDLE';
  }

  private computeButtonsMode() {
    const state = this.state;
    if (!state) return 'OPPONENT_TURN' as const;
    const isMyTurn = state.players[state.currentPlayerIdx]?.id === this.service.myPlayerId;
    if (!isMyTurn) return 'OPPONENT_TURN' as const;
    if (this.uiMode.kind === 'INSERT_DRAGGING') return 'INSERT_DRAG' as const;
    if (this.uiMode.kind === 'PLACEMENT') {
      return this.uiMode.selectedIdx !== null
        ? ('PLACEMENT_SELECTED' as const)
        : ('HOLDING' as const);
    }
    return 'IDLE' as const;
  }
}
